// SuperAdminActivity.jsx — super-admin-only cross-tenant activity log
// viewer at /super-admin-activity. Wraps /activity-logs/all which
// returns the most-recent 200 (configurable) ActivityLog rows across
// every company, decorated with company names. Includes simple
// filters (company, package, search q). Polls every 60s.

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { get } from "../../api";

function fmtTime(t) { if (!t) return '—'; try { return new Date(t).toLocaleString(); } catch { return ''; } }

export default function SuperAdminActivity() {
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const isSuperAdmin = !!userInfoRedux?.superAdmin;
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [packageName, setPackageName] = useState('');
  const [companyId, setCompanyId] = useState('');

  const refresh = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const params = new URLSearchParams();
      if (q)           params.set('q', q);
      if (packageName) params.set('packageName', packageName);
      if (companyId)   params.set('companyId', companyId);
      params.set('limit', '200');
      const r = await get(`/activity-logs/all?${params.toString()}`);
      setRows(((r && r.data && r.data.logs) || []));
    } catch (e) { setErr((e && e.message) || 'Failed to load'); }
    finally { setBusy(false); }
  }, [q, packageName, companyId]);

  useEffect(() => { if (isSuperAdmin) refresh(); }, [isSuperAdmin, refresh]);
  useEffect(() => { if (!isSuperAdmin) return; const t = setInterval(refresh, 60_000); return () => clearInterval(t); }, [isSuperAdmin, refresh]);

  if (!isSuperAdmin) return <Redirect to="/dashboard" />;

  // Unique packages + companies pulled from the loaded set, for the filter dropdowns.
  const packages = Array.from(new Set(rows.map((r) => r.packageName).filter(Boolean))).sort();
  const companies = Array.from(new Map(rows.map((r) => [String(r.companyId), r.companyName || String(r.companyId)])).entries());

  return (
    <div className="container py-4">
      <style>{`
        .sa-card { background: #fff; border: 1px solid #e5e5e5; border-radius: 8px; padding: 14px; margin-bottom: 12px; }
        .sa-table { width: 100%; font-size: 13px; }
        .sa-table th { font-size: 11px; color: #888; text-transform: uppercase; text-align: left; font-weight: 500; border-bottom: 1px solid #f1f1f1; padding: 6px 8px; }
        .sa-table td { padding: 6px 8px; border-bottom: 1px solid #f4f4f4; vertical-align: top; }
        .sa-pill { display: inline-block; font-size: 11px; padding: 1px 8px; border-radius: 10px; background: #eef2ff; color: #15172b; }
        .sa-filters { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
        .sa-filters input, .sa-filters select { fontSize: 12px; padding: 4px 8px; border: 1px solid #ddd; border-radius: 4px; }
      `}</style>

      <div className="d-flex align-items-baseline justify-content-between mb-3">
        <h3 className="mb-0">Activity Log — All Tenants</h3>
        <span style={{ fontSize: 12, color: '#888' }}>super admin · {rows.length} rows · auto-refresh 60s</span>
      </div>

      <div className="sa-filters">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="search task…" style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, width: 220 }} />
        <select value={packageName} onChange={(e) => setPackageName(e.target.value)} style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4 }}>
          <option value="">all packages</option>
          {packages.map((p) => (<option key={p} value={p}>{p}</option>))}
        </select>
        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, minWidth: 220 }}>
          <option value="">all companies</option>
          {companies.map(([id, name]) => (<option key={id} value={id}>{name}</option>))}
        </select>
        <button className="acc-btn" onClick={refresh} style={{ fontSize: 12, padding: '4px 12px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer' }} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</button>
      </div>

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

      <div className="sa-card">
        <table className="sa-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Company</th>
              <th>User</th>
              <th>Package</th>
              <th>Task</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (<tr><td colSpan="5" style={{ color: '#999', padding: 20, textAlign: 'center' }}>{busy ? 'Loading…' : 'No activity matches the filters.'}</td></tr>)}
            {rows.map((r) => (
              <tr key={r._id}>
                <td style={{ whiteSpace: 'nowrap', color: '#888' }}>{fmtTime(r.date)}</td>
                <td>{r.companyName}</td>
                <td style={{ fontSize: 11, fontFamily: 'monospace' }}>{r.userId ? String(r.userId).slice(-8) : '—'}</td>
                <td><span className="sa-pill">{r.packageName || '—'}</span></td>
                <td>{r.task}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
